import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Session, SupabaseClient, User } from "@supabase/supabase-js";

type SupabaseAuthStatus = "loading" | "signed-in" | "needs-email" | "signed-out";

const NEW_USER_WINDOW_MS = 60 * 1000;

export const hasConfirmedEmail = (user: User) => !!user.email && !!user.email_confirmed_at;

export const isNewUser = (user: User) =>
  !!user.last_sign_in_at && Date.parse(user.last_sign_in_at) - Date.parse(user.created_at) < NEW_USER_WINDOW_MS;

type SupabaseAuthContextValue = {
  client: SupabaseClient;
  session: Session | null;
  status: SupabaseAuthStatus;
};

const SupabaseAuthContext = createContext<SupabaseAuthContextValue | null>(null);

export const SupabaseAuthProvider = ({ client, children }: React.PropsWithChildren<{ client: SupabaseClient }>) => {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<SupabaseAuthStatus>("loading");

  useEffect(() => {
    const { data } = client.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setStatus(!nextSession ? "signed-out" : hasConfirmedEmail(nextSession.user) ? "signed-in" : "needs-email");
    });
    return () => data.subscription.unsubscribe();
  }, [client]);

  const value = useMemo(() => ({ client, session, status }), [client, session, status]);

  return <SupabaseAuthContext.Provider value={value}>{children}</SupabaseAuthContext.Provider>;
};

export function useSupabaseAuth() {
  const value = useContext(SupabaseAuthContext);
  if (!value) {
    throw new Error("useSupabaseAuth must be used within a SupabaseAuthProvider");
  }
  return value;
}
